import moment from 'moment-timezone';

import { formatDateDisplay, formatTime, isTimestampBefore } from 'utils/datetime';

/**
 * Converts a UTC ISO timestamp ("2026-06-30T08:00:00Z") into a
 * `datetime-local`-compatible string ("YYYY-MM-DDTHH:mm") in the user's
 * local timezone. Returns "" for invalid inputs.
 *
 * No equivalent in `utils/datetime` — the `datetime-local` input format
 * is unique to this feature. Uses moment for consistency with the rest of
 * the codebase.
 */
export const toDatetimeLocalString = (iso) => {
	if (!iso || typeof iso !== 'string') return '';
	const m = moment(iso, moment.ISO_8601, true);
	if (!m.isValid()) return '';
	return m.format('YYYY-MM-DDTHH:mm');
};

/**
 * Human-readable local rendering for hint text. Composes the locale-aware
 * date + time display; no timezone suffix.
 */
export const formatHumanLocal = (iso) => {
	if (!iso || typeof iso !== 'string') return '';
	if (!moment(iso, moment.ISO_8601, true).isValid()) return '';
	return `${formatDateDisplay(iso, { style: 'dotted' })} ${formatTime(iso)}`;
};

/**
 * Splits an ISO-like timestamp (`YYYY-MM-DDTHH:mm[:ss][Z]`) into a
 * `[date, time]` pair suitable for a split `<Input type="date">` /
 * `<Input type="time">` control. Returns `['', '']` for null / empty input.
 *
 * When `timezone` is provided the UTC ISO is converted to that IANA timezone
 * before splitting, so the returned pair reflects station-local wall-clock time.
 *
 * @param {string | null | undefined} value
 * @param {string | null | undefined} timezone  IANA timezone string
 * @returns {[string, string]}
 */
export const splitTimestamp = (value, timezone) => {
	if (!value || typeof value !== 'string') return ['', ''];

	if (timezone) {
		const m = moment.tz(value, timezone);

		return [m.format('YYYY-MM-DD'), m.format('HH:mm')];
	}

	const [date, time = ''] = value.split('T');

	return [date, time.slice(0, 5)];
};

/**
 * Combines `YYYY-MM-DD` + `HH:mm` into a single timestamp. When `timezone`
 * is provided the wall-clock value is converted to a UTC ISO string;
 * otherwise returns the naive `YYYY-MM-DDTHH:mm` string.
 *
 * @param {string} date  YYYY-MM-DD
 * @param {string} time  HH:mm
 * @param {string | null | undefined} timezone  IANA timezone string
 * @returns {string}
 */
export const combineDateTime = (date, time, timezone) => {
	if (!date || !time) return '';

	const naive = `${date}T${time}`;

	if (!timezone) return naive;

	return moment.tz(naive, timezone).toISOString();
};

/**
 * Extracts the `YYYY-MM-DD` prefix from a `YYYY-MM-DDTHH:mm` timestamp for
 * feeding the `min` attribute of `<Input type="date">`. Returns `undefined`
 * for empty or malformed input, so the DOM attribute is dropped rather than
 * rendered as `min=""`.
 *
 * @param {string | null | undefined} timestamp
 * @returns {string | undefined}
 */
export const toDateOnly = (timestamp) => {
	if (!timestamp || typeof timestamp !== 'string') return undefined;
	const [date] = timestamp.split('T');
	return date || undefined;
};

/**
 * `YYYY-MM-DD` for the current moment. When `timezone` is provided, the date
 * is resolved in that IANA timezone; otherwise the user's local timezone is used.
 *
 * @param {string | null | undefined} timezone IANA timezone string
 */
export const getNowDate = (timezone) => {
	return timezone ? moment().tz(timezone).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
};

/**
 * `HH:mm` for the current moment. When `timezone` is provided, the time is
 * resolved in that IANA timezone; otherwise the user's local timezone is used.
 *
 * @param {string | null | undefined} timezone IANA timezone string
 */
export const getNowTime = (timezone) => {
	return timezone ? moment().tz(timezone).format('HH:mm') : moment().format('HH:mm');
};

/**
 * Converts a naive `YYYY-MM-DDTHH:mm` timestamp to a UTC ISO string by
 * interpreting it in the given IANA timezone. Returns `''` when either
 * argument is missing or empty.
 *
 * @param {string | null | undefined} naiveTimestamp
 * @param {string | null | undefined} timezone IANA timezone string
 * @returns {string}
 */
export const naiveToUtc = (naiveTimestamp, timezone) => {
	if (!naiveTimestamp || !timezone) return '';
	return moment.tz(naiveTimestamp, timezone).toISOString();
};

/**
 * Converts a UTC ISO timestamp to a naive `YYYY-MM-DDTHH:mm` string in
 * the given IANA timezone. Returns `''` when either argument is missing.
 *
 * @param {string | null | undefined} utcTimestamp
 * @param {string | null | undefined} timezone IANA timezone string
 * @returns {string}
 */
export const utcToNaive = (utcTimestamp, timezone) => {
	if (!utcTimestamp || !timezone) return utcTimestamp || '';
	const m = moment.tz(utcTimestamp, timezone);
	if (!m.isValid()) return '';
	return m.format('YYYY-MM-DDTHH:mm');
};

/**
 * Incident time must be at or after the chosen segment's departure.
 * No upper bound — incidents may be reported after the segment ends.
 * Uses `isTimestampBefore` from `utils/datetime`.
 */
export const isIncidentTimestampValid = (value, segment) => {
	if (!value || !segment?.fromTimestamp) return false;
	return !isTimestampBefore(value, segment.fromTimestamp);
};
